import { Query, JsonApiRequestError, Mutation } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { ComboboxData, ComboboxItem } from '@mantine/core';
import { sample, Store } from 'effector';
import { or } from 'patronum';
import { accountTreeModel } from 'entities/account';
import { CASHDESK_NAME_MIN_LENGTH, CASHDESK_NOTE_MAX_LENGTH } from 'entities/cashdesk';
import {
	AccountWithWalletsAndPermissions,
	AddCashdeskMutationParams,
	createAddCashdeskMutation,
	createGetAccountQuery,
	createGetAccountsQuery,
	createGetCashiersQuery,
	EntityWithName,
	IdResponse,
} from 'shared/api';
import {
	createModalFormModel,
	maxLengthRule,
	minLengthRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type AddCashdeskModelFormValue = {
	name: string;
	accountId: number | null;
	currency: string | null;
	note: string;
	cashiers: number[];
};

export type AddCashdeskModel = ModalFormModel<null, AddCashdeskModelFormValue> & {
	getAccountsQuery: Query<void, EntityWithName[], JsonApiRequestError>;
	getAccountQuery: Query<number, AccountWithWalletsAndPermissions, JsonApiRequestError>;
	addCashdeskMutation: Mutation<AddCashdeskMutationParams, IdResponse, JsonApiRequestError>;
	getCashiersQuery: Query<number, EntityWithName[], JsonApiRequestError>;
	$accounts: Store<ComboboxData>;
	$currencies: Store<string[]>;
	$cashiers: Store<ComboboxItem[]>;
	$pending: Store<boolean>;
};

export const createAddCashdeskModel = (): AddCashdeskModel => {
	const getAccountsQuery = createGetAccountsQuery();

	const getAccountQuery = createGetAccountQuery();

	const addCashdeskMutation = createAddCashdeskMutation();

	const getCashiersQuery = createGetCashiersQuery();

	const model = createModalFormModel<null, AddCashdeskModelFormValue>({
		fields: {
			name: {
				init: '',
				rules: [
					notEmptyRule,
					minLengthRule(CASHDESK_NAME_MIN_LENGTH),
					maxLengthRule(CASHDESK_NOTE_MAX_LENGTH),
				],
			},
			accountId: {
				init: null,
				rules: [notEmptyRule],
			},
			currency: {
				init: null,
				rules: [notEmptyRule],
			},
			cashiers: {
				init: [],
			},
			note: {
				init: '',
				rules: [maxLengthRule(CASHDESK_NOTE_MAX_LENGTH)],
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	const $accounts = getAccountsQuery.$data.map((result) =>
		result ? result.map((account) => ({ value: `${account.id}`, label: account.name })) : [],
	);

	const $currencies = getAccountQuery.$data.map((data) =>
		data ? data.wallets.filter(({ isLocked }) => !isLocked).map((wallet) => wallet.currency) : [],
	);

	const $cashiers = getCashiersQuery.$data.map((result) =>
		result ? result.map((cashier) => ({ value: `${cashier.id}`, label: cashier.name })) : [],
	);

	const $pending = or(
		getCashiersQuery.$pending,
		getAccountsQuery.$pending,
		getAccountQuery.$pending,
		addCashdeskMutation.$pending,
	);

	sample({
		clock: model.modalButtonClicked,
		target: [
			model.form.fields.accountId.disable,
			model.form.fields.currency.disable,
			getAccountsQuery.start,
		],
	});

	sample({
		clock: model.modalButtonClicked,
		source: accountTreeModel.$singleSelectedNode,
		filter: (accountId): accountId is number => accountId !== null,
		target: model.form.fields.accountId.setValue,
	});

	sample({
		clock: [model.form.fields.accountId.change, model.form.fields.accountId.setValue],
		target: [
			model.form.fields.currency.disable,
			model.form.fields.cashiers.disable,
			model.form.fields.currency.reset,
			model.form.fields.cashiers.reset,
		],
	});

	sample({
		clock: [model.form.fields.accountId.change, model.form.fields.accountId.setValue],
		filter: Boolean,
		target: [getAccountQuery.start, getCashiersQuery.start],
	});

	chain(getAccountsQuery.finished.success, model.form.fields.accountId.enable);

	sample({
		clock: getAccountQuery.finished.success,
		target: [model.form.fields.currency.enable, model.form.fields.cashiers.enable],
	});

	sample({
		clock: model.form.submitted,
		source: model.form.$values,
		fn: (values) => ({
			name: values.name,
			currency: values.currency!,
			accountId: values.accountId!,
			cashierIds: values.cashiers,
			note: values.note,
		}),
		target: addCashdeskMutation.start,
	});

	chain(addCashdeskMutation.finished.success, model.switcher.disable);

	notify(addCashdeskMutation.finished.success, 'add-cashdesk.success');

	sample({
		clock: model.modalClosed,
		target: [addCashdeskMutation.reset, getAccountQuery.reset, getAccountsQuery.reset],
	});

	return {
		getAccountsQuery,
		getAccountQuery,
		getCashiersQuery,
		addCashdeskMutation,
		$accounts,
		$currencies,
		$cashiers,
		$pending,
		...model,
	};
};
